import { useCallback } from 'react';
import { TFunction } from 'i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useModal } from '../../../hooks/useDialog';
import useAuthForms from '../../../hooks/useAuthForms';
import useLocalization from '../../../hooks/useLocalization';
import { useMutation } from '@tanstack/react-query';
import { QUERY_KEYS } from '../../../constants';
import { postData } from '../../../utils/api';
import StyledForm from '../../forms/shared/StyledForm';
import { enqueueSnackbar } from 'notistack';
import FormTextField from '../../molecules/form-elements/FormTextField';

const getForgotPasswordSchema = (t: TFunction) => {
  return z.object({
    email: z.email(t('register.validation.invalidEmail')),
  });
};

type ForgotPasswordData = z.infer<ReturnType<typeof getForgotPasswordSchema>>;

const ForgotPasswordForm = () => {
  const { t } = useLocalization();
  const { closeModal } = useModal();
  const { openLoginForm } = useAuthForms();

  const forgotPasswordSchema = getForgotPasswordSchema(t);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<ForgotPasswordData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: '',
    },
  });

  const { mutate: forgotPassword } = useMutation({
    mutationKey: [QUERY_KEYS.forgotPassword],
    mutationFn: (data: ForgotPasswordData) => postData('customer/forgot-password', { email: data.email }, 'crm'),
    onSuccess: () => {
      enqueueSnackbar(t('forgotPassword.emailSent'), { variant: 'success' });
      handleClose();
    },
    onError: () => {
      enqueueSnackbar(t('unexpectedError'), { variant: 'error' });
    },
  });

  const handleClose = useCallback(() => {
    reset();
    closeModal();
  }, [reset, closeModal]);

  const onSubmit = (data: ForgotPasswordData) => {
    forgotPassword(data);
  };

  return (
    <StyledForm
      onSubmit={handleSubmit(onSubmit)}
      onClose={handleClose}
      onBack={openLoginForm}
      submitTitle={t('forgotPassword.sendResetLink')}
      title={t('forgotPassword.title')}
      isSubmitting={isSubmitting}
    >
      <FormTextField
        name='email'
        control={control}
        label={t('register.email')}
        error={!!errors.email}
        helperText={errors.email?.message}
        disabled={isSubmitting}
        sx={{ minWidth: { xs: '100%', md: 400 } }}
      />
    </StyledForm>
  );
};

export default ForgotPasswordForm;
